import React from 'react';
import GameDescriptionBlock from '../gameDescBlock';
import BadgesGrid from '../gameBadgesGrid';
import Overview from '../overview';
import GameFeatures from '../gameFeatures';
import SimilarGames from '../infoBlocks/similarGamesBlock';
import { useParams, Navigate, NavLink } from 'react-router-dom';
import { useGetGameQuery } from '../../redux/api/games';
import { usePageTitle } from '../../utils/hooks/usePageTitle';
import { useMetaTags } from '../../utils/hooks/useMetaTags';
import MobileScreenshotWrapper from '../../uiKit/MobileScreenshotWrapper';
import GameReviews from '../gameReviews';
import HeroBlock from '../heroBlock';
import Title from '../../uiKit/typography/title';
import { useTranslation } from 'react-i18next';
import PartnersBlock from '../infoBlocks/partnersEpicBlock';
import PartnersSlider from '../infoBlocks/partnersSlider';

export default React.memo(function GamePage({ data }) {
  const params = useParams();
  const { t } = useTranslation('common');
  let { data: game, isFetching } = useGetGameQuery(params.slug, { skip: !!data });
  game = data ?? game;

  usePageTitle(game?.name);
  useMetaTags([
    { name: 'description', content: game?.overview || '' },
    { property: 'og:title', content: game?.name || '' },
    { property: 'og:description', content: game?.overview.slice(0, 150) + '...' || '' },
    { property: 'og:image', content: game?.imagePreview || '' },
    { name: 'twitter:title', content: game?.name || '' },
    { name: 'twitter:description', content: game?.overview.slice(0, 150) + '...' || '' },
    { name: 'twitter:image', content: game?.imagePreview || '' },

  ]);

  const isVertical = game?.screenshotsView === 'vertical';

  return isFetching ? null : !game ? <Navigate to={'/404'} replace={true} /> : (
    <>
      <HeroBlock showOverlay={false} showImage={false} className={'grid'} childWrapperClassName={'!items-start !pb-20'}>
        <div className={'w-full h-full flex flex-col items-center gap-10'}>
          <div className={'text-left px-4 md:px-10 mr-auto'}>
            <span className={'whitespace-pre-line text-white font-medium'}><NavLink to={'/games'}>{t('navigation.games')}</NavLink> / {game.name}</span>
          </div>
          <div className={'w-full h-full px-4 md:px-10'}>
            <GameDescriptionBlock game={game} />
          </div>
        </div>
      </HeroBlock>

      <div className={'w-full grid grid-cols-1 gap-6 lg:grid-cols-2 pt-[5.625rem] pb-15 px-4 md:px-10'}>
        <Overview text={game.overview} />
        <BadgesGrid game={game} />
      </div>

      {game.featuresInfo && !!game.featuresInfo.length &&
        <div className={'w-full pt-20 md:py-28 px-4 md:px-10 xl:px-44'}>
          <GameFeatures features={game.featuresInfo} />
        </div>}

      {game.gameplayScreenshots &&
        <div className={'w-full py-20 px-4 md:px-10 xl:px-44'}>
          <div className={'w-full flex flex-col items-center gap-8'}>
            <Title className={'text-3xl'}>
              {t('titles.screenshots')}
            </Title>

            <div className={'w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 place-content-center place-items-center gap-x-6 gap-y-8'}>
              {game.gameplayScreenshots.map(src =>
                isVertical ?
                  <MobileScreenshotWrapper key={src} src={src} width={240} height={496} /> :
                  <img key={src} src={src} alt={`${game.name} gameplay`}
                       className={`object-cover w-full h-full rounded-[1.25rem]`}
                       draggable={false} />
              )}
            </div>
          </div>
        </div>
      }

      <PartnersSlider className={'py-15'} />
      <PartnersBlock />

      {game.reviews && !!game.reviews.length &&
        <div className={'w-full flex justify-center py-10 md:py-15 px-4 md:px-12 xl:px-44'}>
          <GameReviews data={game.reviews} />
        </div>
      }

      {game.similarGames && !!game.similarGames.length && // temp while filling db
        <div className={'w-full flex justify-center py-10 md:py-15 px-4 md:px-12'}>
        <SimilarGames games={game.similarGames} />
      </div>
      }

    </>
  );
})
